import axios from 'axios';
import { getMVToken } from '@/helpers/auth';
import { Urls } from '@/helpers/api-urls';

// Per-request config rather than useAxiosPost: these reads are chained (an operator's
// fleets, then each document's presigned URL) and need a returned value, and mutating
// axios.defaults for a Maverick call leaks into unrelated Phoenix requests.
const mvRequest = (method, url, data) =>
  axios({
    method,
    url,
    ...(data === undefined ? {} : { data }),
    baseURL: process.env.REACT_APP_API_BASE_URL_MAVERICK,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getMVToken()}`,
    },
  }).then((response) => response?.data?.responseData);

export const fetchOperator = (operatorId) => mvRequest('get', Urls.GetOperatorById(operatorId));

export const fetchAircraft = (aircraftId) => mvRequest('get', Urls.GetAircraftById(aircraftId));

export const fetchFleetsByOperator = (operatorId) =>
  mvRequest('get', Urls.GetFleetsByOperator(operatorId)).then((data) => data ?? []);

export const fetchFleet = (fleetId) => mvRequest('get', Urls.GetFleetById(fleetId));

// Documents come back as S3 coordinates, not links, so each one is exchanged for a
// presigned URL. Settled per file, not all-or-nothing: one key that fails to sign would
// otherwise take every other document and photo down with it, so a failed file keeps
// whatever URL it arrived with rather than dropping out of the list.
export const fetchFileLinks = async (files) => {
  if (!files || files.length === 0) {
    return [];
  }
  const settled = await Promise.allSettled(
    files.map((file) => {
      const awsFilePath = encodeURIComponent(`${file.path}/${file.hash}/${file.name}`);
      return mvRequest('get', `${Urls.DownloadIdentityInformation}?awsFilePath=${awsFilePath}`);
    })
  );
  return files
    .map((file, index) => {
      const result = settled[index];
      const signedUrl = result.status === 'fulfilled' ? result.value : null;
      return {
        ...file,
        fullUrl: signedUrl ?? file.fullUrl,
      };
    })
    // Core allows a file's fullUrl to be null, so a file that failed to sign and arrived
    // without one has nothing to point at. Dropping it beats rendering a broken thumbnail
    // or a dead download link next to the files that did sign.
    .filter((file) => Boolean(file.fullUrl));
};
